import React, { useState } from 'react';
import { Table } from 'react-bootstrap';
import { InternalApiScreeningAlertStatus } from '../../../api-internal';
import { ApiQuickStatusComment } from '../../../api';
import * as Yup from 'yup';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import {
  SalvBanner,
  SalvBannerType,
  SalvBannerAccent,
  SalvButton,
  SalvModal,
  SalvTextField,
  Size,
} from '@salvapp/ui-components';
import QuickStatusNoteRow from './QuickStatusNoteRow';
import { useQuickStatusNotes } from './useQuickStatusNotes';
import immUpdate from 'immutability-helper';
import './QuickStatusNotesModal.scss';
import { quickStatusCommentApi } from '../../../apis';
import { fetchQuickStatusComments } from './statusCommentsAction';
import { useDispatch } from 'react-redux';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';
import { useMe } from '../../../organisation/useMe';

const validationSchema = Yup.object().shape({
  notes: Yup.array().of(
    Yup.object().shape({
      id: Yup.number(),
      comment: Yup.string().required(),
      isNew: Yup.boolean().required().default(false),
      key: Yup.number().default(Math.random()),
    })
  ),
  removedNotes: Yup.array().of(Yup.number()),
});

interface Props {
  showModal: boolean;
  onClose: () => void;
  status: InternalApiScreeningAlertStatus;
}

const QuickStatusNotesModal = ({ showModal, onClose, status }: Props) => {
  const dispatch = useDispatch();
  const [newNote, setNewNote] = useState<string>('');
  const [quickStatusNotes, _] = useQuickStatusNotes();
  const { features } = useMe();
  const bridgeAlertsEnabled = isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_INCOMING, features);

  const initialValues = {
    notes: quickStatusNotes
      .filter((comment: ApiQuickStatusComment) => comment.status === status.code)
      .map((comment: ApiQuickStatusComment) => {
        return {
          id: comment.id,
          comment: comment.comment,
          isNew: false,
        };
      }),
    removedNotes: [] as number[],
  };

  const screeningEnabled = status._configuration?.screeningLists?.enabled;
  const monitoringEnabled = status._configuration?.monitoring?.enabled;
  const bridgeEnabled = bridgeAlertsEnabled && status._configuration?.bridge?.enabled;

  const hasMultipleTypes = [screeningEnabled, monitoringEnabled, bridgeEnabled].filter((it) => !!it).length > 1;

  type FormikValues = typeof initialValues;

  const submit = async (values: FormikValues, { setSubmitting }: FormikHelpers<FormikValues>) => {
    try {
      setSubmitting(true);
      const { notes, removedNotes } = values;

      await Promise.all(
        removedNotes.map(async (id) => {
          return await quickStatusCommentApi.deleteStatusComment({
            id,
          });
        })
      );

      await Promise.all(
        notes.map(async (note, index) => {
          if (note.isNew) {
            return await quickStatusCommentApi.addStatusComment({
              apiQuickStatusComment: {
                status: status.code,
                comment: note.comment,
                createdTime: new Date(),
                sortOrder: index + 1,
              },
            });
          }

          return await quickStatusCommentApi.updateStatusComment({
            apiQuickStatusComment: {
              id: note.id,
              status: status.code,
              comment: note.comment,
              createdTime: new Date(),
              sortOrder: index + 1,
            },
          });
        })
      );
      dispatch(fetchQuickStatusComments());
      onClose();
    } catch (_) {
      // empty
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validateOnMount
      onSubmit={submit}
      validationSchema={validationSchema}
    >
      {({ values, isSubmitting, setFieldValue, isValid, submitForm }: FormikProps<FormikValues>) => {
        const addNote = () => {
          setFieldValue('notes', [...values.notes, { comment: newNote, isNew: true, key: Math.random() }]);
          setNewNote('');
        };

        return (
          <SalvModal show={showModal} onHide={onClose}>
            <SalvModal.Header closeButton>
              <div className='title-s'>Quick Status Notes</div>
            </SalvModal.Header>
            <SalvModal.Body>
              <div className='text-s'>
                Quick Status Notes are useful if you are often adding the same few notes when selecting a status for an
                alert. These notes appear in the Alert notes box with the status change.
              </div>
              <Form>
                <div className='d-flex m-t-16'>
                  <SalvTextField
                    className='w-100 p-r-8'
                    ariaLabel='new note'
                    placeholder='Add Quick Status Note...'
                    value={newNote}
                    size={Size.MEDIUM}
                    onChange={setNewNote}
                  />
                  <SalvButton label='Add' type='submit' disabled={!newNote} size={Size.MEDIUM} onClick={addNote} />
                </div>
              </Form>
              <Table striped size='sm' className='quick-status-notes-table card-table m-t-16'>
                <tbody>
                  {values.notes.map((note: any, index: number) => {
                    const removeRow = (id: string | undefined, index: number) => {
                      const newValues = [...values.notes];
                      newValues.splice(index, 1);
                      setFieldValue('notes', newValues);
                      if (id) {
                        setFieldValue('removedNotes', [...values.removedNotes, id]);
                      }
                    };
                    const updateRow = (value: string) => {
                      const copiedValues = [...values.notes];
                      copiedValues[index] = {
                        ...note,
                        comment: value,
                      };
                      setFieldValue('notes', copiedValues);
                    };

                    const moveField = (dragIndex: number, hoverIndex: number) => {
                      const dragCard = values.notes[dragIndex];
                      const newFields = immUpdate(values.notes, {
                        $splice: [
                          [dragIndex, 1],
                          [hoverIndex, 0, dragCard],
                        ],
                      });
                      setFieldValue('notes', newFields);
                    };

                    return (
                      <QuickStatusNoteRow
                        key={note.id ?? note.key}
                        id={note.id ?? note.key}
                        note={note.comment}
                        index={index}
                        onChange={updateRow}
                        onRemove={() => removeRow(note.id, index)}
                        moveField={moveField}
                      />
                    );
                  })}
                </tbody>
              </Table>
              {hasMultipleTypes && (
                <SalvBanner
                  className='quick-status-notes-banner m-t-16'
                  type={SalvBannerType.WARNING}
                  accent={SalvBannerAccent.LIGHT}
                  message={`This status is used for other alert and/or entity types. Adding these Quick Status Notes will affect these alert types:
                    ${screeningEnabled ? '• Screening' : ''}
                    ${monitoringEnabled ? '• Monitoring' : ''}
                    ${bridgeEnabled ? '• Bridge' : ''}`}
                />
              )}
            </SalvModal.Body>
            <SalvModal.Footer>
              <SalvButton label='Close' onClick={onClose} size={Size.LARGE} variant='muted' />
              <SalvButton
                disabled={isSubmitting || !isValid}
                size={Size.LARGE}
                type='submit'
                label='Save'
                variant='primary'
                onClick={submitForm}
              />
            </SalvModal.Footer>
          </SalvModal>
        );
      }}
    </Formik>
  );
};
export default QuickStatusNotesModal;
